import { useExperimentalFeature } from "@istari-frontend/experimental-features";
import type { ReactNode } from "react";
import { Navigate, useParams } from "react-router";

import { routes } from "~/routes";

type ExperimentalDashboardGuardProps = {
  feature: "resource-redesign" | "system-redesign";
  redirectTo: string;
  children: ReactNode;
};

function ExperimentalDashboardGuard({ feature, redirectTo, children }: ExperimentalDashboardGuardProps) {
  const { available } = useExperimentalFeature(feature);
  if (!available) {
    return <Navigate to={redirectTo} replace />;
  }
  return <>{children}</>;
}

export function ResourcesDashboardGuard({ children }: { children: ReactNode }) {
  return (
    <ExperimentalDashboardGuard feature="resource-redesign" redirectTo={routes.Resources}>
      {children}
    </ExperimentalDashboardGuard>
  );
}

export function SystemsDashboardGuard({ children }: { children: ReactNode }) {
  // Availability, not the user's toggle: the route stays reachable for a user who has the redesign
  // switched off, so a link into the dashboard still works.
  return (
    <ExperimentalDashboardGuard feature="system-redesign" redirectTo={routes.Systems}>
      {children}
    </ExperimentalDashboardGuard>
  );
}

// Wraps every resource route, not just the connected one: a guard on one route alone changes the
// element type at that position, so routing between them would remount the page and drop the tree's
// expansion, scroll and selection.
export function ConnectedResourceGuard({ children }: { children: ReactNode }) {
  const { connectedResourceId } = useParams();
  // The user's own toggle here, not availability: with the feature off the connected lookup behind
  // this page is disabled, so the route would render a resource page that can resolve nothing.
  const { enabled } = useExperimentalFeature("connected-sources");
  if (connectedResourceId !== undefined && !enabled) {
    return <Navigate to={routes.Resources} replace />;
  }
  return <>{children}</>;
}
